import { PlanName } from '@services/regimen-service/regimen.service';

export const planInfoItemId = {
  PRODUCTS: 'products',
  MONTHLY_CHECKUPS: 'monthly_checkups',
  DEDICATED_DOCTOR: 'dedicated_doctor',
  DIET_PLAN: 'diet_plan',
  NUTRITIONIST_SESSION: 'nutritionist_session',
  NUTRITIONIST_SESSIONS: 'nutritionist_sessions',
  ONE_ON_ONE_DOCTOR: 'one_on_one_doctor',
  MONEY_BACK_GUARANTEE: 'money_back_guarantee',
  DIET_PLUS_ONE_CALL_NUTRITIONIST: 'diet_plus_one_call_nutritionist',
  DIET_PLUS_TWO_CALLS_NUTRITIONIST: 'diet_plus_two_calls_nutritionist',
} as const;

export const planInfoImageUrls = {
  CHECKUP_REPORT_SQUARE: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/checkup_report_square.png',
  CALL_ICONS_SQUARE: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/call_icons_square.png',
  DIET_PLAN_SQUARE: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/diet_plan_square.png',
} as const;

export interface PlanInfoItem {
  id: string;
  imageUrl: string;
  title: string; // Localized title using $localize
  description: string; // Localized description using $localize
}

export interface PlanInfoConfig {
  items: PlanInfoItem[];
}

function insertAt<T>(array: T[], index: number, item: T): T[] {
  const result = [...array];
  result.splice(index, 0, item);
  return result;
}

export function getPlanInfoConfig(
  fromRegimenProposition: boolean = false,
  enabledPlans: PlanName[] = []
): Record<PlanName, PlanInfoConfig> {
  const items: Record<PlanName, PlanInfoConfig> = {
    advance: {
      items: [
        {
          id: planInfoItemId.PRODUCTS,
          imageUrl: '',
          title: $localize`:@@PRODUCT_TREATMENT_KIT:product Treatment Kit`,
          description: $localize`:@@CUSTOMISED_KIT_PRODUCTS:A customised kit of {$PRODUCT_COUNT} products for your skin concerns.`,
        },
        {
          id: planInfoItemId.MONTHLY_CHECKUPS,
          imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/checkup_report.png',
          title: $localize`:@@MONTHLY_DOCTOR_CHECKUPS:Monthly Doctor Checkups`,
          description: $localize`:@@DETAILED_MONTHLY_CHECKUPS:Detailed monthly checkups by CureSkin doctor to track progress.`,
        },
        {
          id: planInfoItemId.DEDICATED_DOCTOR,
          imageUrl: '',
          title: $localize`:@@DEDICATED_DOCTOR_SUPPORT:Dedicated Doctor for Support`,
          description: $localize`:@@ONE_DOCTOR_GUIDING_CONSISTENTLY:One doctor guiding you consistently throughout your treatment journey.`,
        },
        {
          id: planInfoItemId.DIET_PLAN,
          imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/diet_plan.png',
          title: $localize`:@@DIET_PLAN:Diet Plan`,
          description: $localize`:@@PERSONALISED_DIET_PLAN_FASTER:A personalised diet plan supporting faster, long-term skin results.`,
        },
      ],
    },
    pro: {
      items: [
        {
          id: planInfoItemId.PRODUCTS,
          imageUrl: '',
          title: $localize`:@@PRODUCT_TREATMENT_KIT:product Treatment Kit`,
          description: $localize`:@@CUSTOMISED_KIT_PRODUCTS:A customised kit of {$PRODUCT_COUNT} products for your skin concerns.`,
        },
        {
          id: planInfoItemId.NUTRITIONIST_SESSION,
          imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/call_icons.png',
          title: $localize`:@@ONE_SESSION_NUTRITIONIST:1 session with nutritionist`,
          description: $localize`:@@PERSONALISED_NUTRITION_SESSION_DIET:A personalised nutrition session to optimise your diet for better skin.`,
        },
        {
          id: planInfoItemId.MONTHLY_CHECKUPS,
          imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/checkup_report.png',
          title: $localize`:@@MONTHLY_DOCTOR_CHECKUPS:Monthly Doctor Checkups`,
          description: $localize`:@@DETAILED_MONTHLY_CHECKUPS:Detailed monthly checkups by CureSkin doctor to track progress.`,
        },
        {
          id: planInfoItemId.DEDICATED_DOCTOR,
          imageUrl: '',
          title: $localize`:@@DEDICATED_DOCTOR_SUPPORT:Dedicated Doctor for Support`,
          description: $localize`:@@ONE_DOCTOR_GUIDING_CONSISTENTLY:One doctor guiding you consistently throughout your treatment journey.`,
        },
        {
          id: planInfoItemId.DIET_PLAN,
          imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/diet_plan.png',
          title: $localize`:@@DIET_PLAN:Diet Plan`,
          description: $localize`:@@PERSONALISED_DIET_PLAN_FASTER:A personalised diet plan supporting faster, long-term skin results.`,
        },
      ],
    },
    assist: {
      items: [
        {
          id: planInfoItemId.PRODUCTS,
          imageUrl: '',
          title: $localize`:@@PRODUCT_TREATMENT_KIT:product Treatment Kit`,
          description: $localize`:@@CUSTOMISED_KIT_PRODUCTS:A customised kit of {$PRODUCT_COUNT} products for your skin concerns.`,
        },
        {
          id: planInfoItemId.ONE_ON_ONE_DOCTOR,
          imageUrl: '',
          title: $localize`:@@ONE_TO_ONE_DOCTOR_CONSULTATION:1:1 doctor consultation`,
          description: $localize`:@@ONE_DOCTOR_GUIDING_CONSISTENTLY:One doctor guiding you consistently throughout your treatment journey.`,
        },
        {
          id: planInfoItemId.NUTRITIONIST_SESSIONS,
          imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/call_icons.png',
          title: $localize`:@@TWO_SESSIONS_NUTRITIONIST:2 sessions with nutritionist`,
          description: $localize`:@@TWO_PERSONALISED_NUTRITION_SESSIONS:2 personalised nutrition sessions to optimise your diet for better skin.`,
        },
        {
          id: planInfoItemId.MONTHLY_CHECKUPS,
          imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/checkup_report.png',
          title: $localize`:@@MONTHLY_DOCTOR_CHECKUPS:Monthly Doctor Checkups`,
          description: $localize`:@@DETAILED_MONTHLY_CHECKUPS:Detailed monthly checkups by CureSkin doctor to track progress.`,
        },
        {
          id: planInfoItemId.DIET_PLAN,
          imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/diet_plan.png',
          title: $localize`:@@DIET_PLAN:Diet Plan`,
          description: $localize`:@@PERSONALISED_DIET_PLAN_FASTER:A personalised diet plan supporting faster, long-term skin results.`,
        },
      ],
    },
    introductory: {
      items: [
        {
          id: planInfoItemId.PRODUCTS,
          imageUrl: '',
          title: $localize`:@@PRODUCT_TREATMENT_KIT:product Treatment Kit`,
          description: $localize`:@@CUSTOMISED_KIT_PRODUCTS:A customised kit of {$PRODUCT_COUNT} products for your skin concerns.`,
        },
        {
          id: planInfoItemId.MONTHLY_CHECKUPS,
          imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/checkup_report.png',
          title: $localize`:@@MONTHLY_DOCTOR_CHECKUPS:Monthly Doctor Checkups`,
          description: $localize`:@@DETAILED_MONTHLY_CHECKUPS:Detailed monthly checkups by CureSkin doctor to track progress.`,
        },
        {
          id: planInfoItemId.DEDICATED_DOCTOR,
          imageUrl: '',
          title: $localize`:@@DEDICATED_DOCTOR_SUPPORT:Dedicated Doctor for Support`,
          description: $localize`:@@ONE_DOCTOR_GUIDING_CONSISTENTLY:One doctor guiding you consistently throughout your treatment journey.`,
        },
        {
          id: planInfoItemId.DIET_PLAN,
          imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/diet_plan.png',
          title: $localize`:@@DIET_PLAN:Diet Plan`,
          description: $localize`:@@PERSONALISED_DIET_PLAN_FASTER:A personalised diet plan supporting faster, long-term skin results.`,
        },
      ],
    },
  };

  const moneyBackGuaranteeItem: PlanInfoItem = {
    id: planInfoItemId.MONEY_BACK_GUARANTEE,
    imageUrl: '/assets/images/money_back_guarantee.png',
    title: $localize`:@@MONEY_BACK_GUARANTEE:100% Money Back Guarantee`,
    description: $localize`:@@MONEY_BACK_GUARANTEE_DESC:100% refund, if no improvement seen after following the plan for given time.`,
  };

  const dietPlusOneCallNutritionistItem: PlanInfoItem = {
    id: planInfoItemId.DIET_PLUS_ONE_CALL_NUTRITIONIST,
    imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/diet_plan_square.png',
    title: $localize`:@@DIET_PLUS_ONE_CALL_NUTRITIONIST:Diet + 1 call with nutritionist`,
    description: $localize`:@@PERSONALISED_NUTRITION_SESSION_DIET:A personalised nutrition session to optimise your diet for better skin.`,
  };

  const dietPlusTwoCallsNutritionistItem: PlanInfoItem = {
    id: planInfoItemId.DIET_PLUS_TWO_CALLS_NUTRITIONIST,
    imageUrl: 'https://cdn.cureskin.com/app/img/regimen-value-proposition-rebranded/diet_plan_square.png',
    title: $localize`:@@DIET_PLUS_TWO_CALLS_NUTRITIONIST:Diet + 2 calls with nutritionist`,
    description: $localize`:@@TWO_PERSONALISED_NUTRITION_SESSIONS:2 personalised nutrition sessions to optimise your diet for better skin.`,
  };

  // Apply money back guarantee to enabled plans
  enabledPlans.forEach((plan) => {
    if (plan === 'pro') {
      // Filter and merge for pro plan
      const proFiltered = items.pro.items.filter(
        item => item.id !== planInfoItemId.DIET_PLAN && item.id !== planInfoItemId.NUTRITIONIST_SESSION
      );
      let proItems = insertAt(proFiltered, 1, dietPlusOneCallNutritionistItem);
      if (fromRegimenProposition) {
        proItems.push(moneyBackGuaranteeItem);
      } else {
        proItems = insertAt(proItems, 1, moneyBackGuaranteeItem);
      }
      items.pro.items = proItems;
    } else if (plan === 'assist') {
      // Filter and merge for assist plan
      const assistFiltered = items.assist.items.filter(
        item => item.id !== planInfoItemId.DIET_PLAN && item.id !== planInfoItemId.NUTRITIONIST_SESSIONS
      );
      let assistItems: PlanInfoItem[];
      if (fromRegimenProposition) {
        assistItems = insertAt(assistFiltered, 2, dietPlusTwoCallsNutritionistItem);
        assistItems.push(moneyBackGuaranteeItem);
      } else {
        assistItems = insertAt(assistFiltered, 1, moneyBackGuaranteeItem);
        assistItems = insertAt(assistItems, 3, dietPlusTwoCallsNutritionistItem);
      }
      items.assist.items = assistItems;
    } else if (plan === 'advance' || plan === 'introductory') {
      // For advance/introductory plans, just add money back guarantee at the end
      if (fromRegimenProposition) {
        items[plan].items.push(moneyBackGuaranteeItem);
      } else {
        items[plan].items.splice(1, 0, moneyBackGuaranteeItem);
      }
    }
  });

  return items;
}

